import * as React from 'react'
import type { HuntLiveDto } from '@/hooks/use-automation'
import { cn } from '@/lib/cn'

/**
 * How far a hunt got, in five words.
 *
 * The same shape as `RunPipeline` — five segments, one plain-English line
 * underneath — because that is the readout operators already know. Deliberately
 * a separate component rather than a mode flag on `RunPipeline`: they share a
 * look, not a data model, and `run-pipeline.tsx` already refused exactly this
 * union once. A parameter of "either a run phase or a hunt state" is how a small
 * component turns into a branch nobody can read.
 *
 * ── The bar reports POSITION, never health ──────────────────────────
 * A stalled worker does not turn Watch amber. Degradation is said in words, in
 * the verdict lines, where it can name which worker and why. Folding "where am
 * I" and "how am I" into one hue makes the bar mean two things and stops it
 * being glanceable — the same discipline that keeps the accent colour out of
 * status everywhere else in this app.
 */

interface Stage {
  key: string
  label: string
}

const STAGES: readonly Stage[] = [
  { key: 'arm', label: 'Arm' }, // endpoints leased, identities claimed
  { key: 'spread', label: 'Spread' }, // workers coming online one by one
  { key: 'watch', label: 'Watch' }, // the steady zero-gap loop: ~all of the time
  { key: 'strike', label: 'Strike' }, // ACTIVE seen, update-application in flight
  { key: 'claimed', label: 'Claimed' }, // the shift is ours
]

/**
 * Which segment this hunt is in, or — once it has ended — which it REACHED.
 *
 * The reached-segment fallback is the same rule `RunPipeline` uses for a failed
 * run, and for the same reason: without it the bar goes blank at exactly the
 * moment someone is reading it to find out where things ended.
 */
export function stageIndex(hunt: HuntLiveDto, reported: number): number {
  if (hunt.state === 'won') return 4
  /*
   * A burst is the same STAGE as a strike — claiming — and lasts minutes rather
   * than milliseconds, which is exactly why it must reach this line. Left out,
   * the bar would fall back to the poll-count rule below and slide backwards to
   * "watching" for the whole of the one stretch the operator is watching.
   */
  if (hunt.state === 'striking' || hunt.state === 'bursting') return 3
  if (reported === 0) return 0
  if (reported < hunt.workers.length) return 1
  return 2
}

interface Style {
  fill: string
  text: string
  pulse: boolean
}

function styleFor(hunt: HuntLiveDto): Style {
  if (!hunt.live) {
    if (hunt.state === 'won') return { fill: 'bg-live', text: 'text-live', pulse: false }
    if (hunt.endedReason?.startsWith('failed') === true) {
      return { fill: 'bg-danger', text: 'text-danger', pulse: false }
    }
    return { fill: 'bg-neutral', text: 'text-muted', pulse: false }
  }
  if (hunt.state === 'striking' || hunt.state === 'bursting') {
    return { fill: 'bg-warn', text: 'text-warn', pulse: true }
  }
  if (hunt.state === 'won') return { fill: 'bg-live', text: 'text-live', pulse: false }
  return { fill: 'bg-live', text: 'text-live', pulse: true }
}

export const HuntStages = React.memo(function HuntStages({
  hunt,
  reported,
  caption,
}: {
  hunt: HuntLiveDto
  reported: number
  caption: string
}) {
  const index = stageIndex(hunt, reported)
  const style = styleFor(hunt)
  const current = STAGES[index]

  return (
    <div
      role="progressbar"
      aria-label="Hunt progress"
      aria-valuemin={0}
      aria-valuemax={STAGES.length}
      aria-valuenow={index + 1}
      aria-valuetext={`${current?.label ?? 'idle'}, stage ${index + 1} of ${STAGES.length}. ${caption}`}
      className="w-full px-3 py-2"
    >
      {/* Phone: only the stage you are in gets a name. Four labels nobody is
          waiting on is four labels of noise on a 360px screen. */}
      <div className="mb-1.5 flex items-center gap-2 sm:hidden">
        <span className={cn('truncate text-[12px] leading-4 font-medium', style.text)}>
          {current?.label ?? '—'}
        </span>
        <span className="tabular ml-auto flex-none text-[12px] leading-4 text-faint">
          {index + 1} of {STAGES.length}
        </span>
      </div>

      <div className="mb-1.5 hidden gap-1.5 sm:grid sm:grid-cols-5">
        {STAGES.map((stage, i) => {
          const isCurrent = index === i
          return (
            <span
              key={stage.key}
              className={cn(
                'flex min-w-0 items-center gap-1.5 text-[12px] leading-4',
                isCurrent ? cn('font-medium', style.text) : 'text-faint'
              )}
            >
              {isCurrent && style.pulse ? <span className="pulse-dot" aria-hidden="true" /> : null}
              <span className="truncate">{stage.label}</span>
            </span>
          )
        })}
      </div>

      {/* A picture of the labels above, so it adds nothing to the a11y tree —
          `aria-valuetext` already says the same thing as a sentence. */}
      <div aria-hidden="true" className="grid grid-cols-5 gap-1.5">
        {STAGES.map((stage, i) => (
          <span
            key={stage.key}
            className={cn(
              'h-[3px] rounded-full transition-colors duration-300',
              index > i && 'bg-live',
              index === i && style.fill,
              index === i && style.pulse && 'animate-pulse',
              index < i && 'bg-s3'
            )}
          />
        ))}
      </div>

      <p className="mt-1.5 truncate text-[12px] leading-[17px] text-muted">{caption}</p>
    </div>
  )
})
